import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaPencilAlt } from 'react-icons/fa'

import { Box, Button, Grid } from '@mui/material'
import { FlagCircle, Print, Visibility } from '@mui/icons-material'

import {
  useGET_lessonPlanOptionsQuery,
  useGET_lessonPlanByIdQuery, 
  usePOST_lessonPlanMutation,
  usePATCH_lessonPlanMutation,
  useDELETE_lessonPlanMutation,
} from 'redux/api/lessonPlansApi'

import {
  useGET_subjectsSelectQuery
} from 'redux/api/subjectsApi'

import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'

import { assessmentTypeSelectData, gradeSelectData, printDiv, translateGradeLevel, lessonNumberSelectData } from 'utils/helpers'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'

import DialogPopup from 'components/molecules/DialogPopup'

import formModel, { initialValues, validationSchema } from 'utils/formModels/lessonPlanModel'

import { crudColorMap, crudIconMap}  from 'utils/helpers'

import { ActionType } from 'types'
import SectionTitle from 'components/atoms/SectionTitle'
import GoalbankPage from 'pages/protected/GoalbankPage'
import _ from 'lodash'
import useCrudData from 'utils/customHooks/useCrudData'
import { useNavigate } from 'react-router-dom'
import LessonPlanTemplate from 'components/utilized/lessonPlans/LessonPlanTemplate'
import { AppDispatch, RootState } from 'redux/store'
import { useDispatch, useSelector } from 'react-redux'
import InfoTooltip from 'components/atoms/buttons/InfoTooltip'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'
import { useGET_unitsSelectQuery } from 'redux/api/unitsApi'

interface IProps {
  action: ActionType
  id: string
  sessionId?: string
  viewOnly: boolean
}

const LessonPlanForm: FC<IProps> = ({ action, id, sessionId, viewOnly }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const user = useSelector((state: RootState) => state['user'].user)

  const backUrl = useSelector((state: RootState) => state['lessonPlans'].lessonPlanBackUrl)
  const backToSlug = backUrl || '/lesson-plans'

  
  var seconds = new Date().getTime() / 1000;
  const editSlug = sessionId ? `/sessions/edit/${sessionId}?t=${seconds}` : backToSlug

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug: editSlug,
    entityName: `${t('common.lesson-plans')}`,
    entityId: id,
    getEntityQuery: useGET_lessonPlanByIdQuery,
    createEntityMutation: usePOST_lessonPlanMutation,
    editEntityMutation: usePATCH_lessonPlanMutation,
    deleteEntityMutation: useDELETE_lessonPlanMutation,
    onSuccess: () => {
      if(!id)
        window.location.replace(editSlug)
    }
  })
  

  const {
    title,
    summary,
    subject,
    grade_level,
    unit,
    number,
    prior_knowledge,
    understandings,
    essential_questions,
    assessment_type,
    assessment_evidence,
    other_evidence,
    diagnostic,
    specific_plan,
    options
  } = formModel()

  const serverValues = entity ? {
    ...entity,
    grade_level: entity.grade_level >= 0 ? {
      id: entity.grade_level,
      title: translateGradeLevel(entity.grade_level, true)
    } : null,
    number: entity.number >= 0 ? {
      id: entity.number,
      title:  entity.number.toString()
    } : null,
    assessment_type: 
      entity.assessment_type 
        ? {id: entity.assessment_type, title: t(`common.${entity.assessment_type}`)} 
        : null,

  } : null

  const formValues = serverValues || initialValues

  const [preview, setPreview] = useState(false)

  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))

    setPreview(!!(entity && entity.id))
  }, [entity])

  return ( <>
        <Formik
          enableReinitialize
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {

            const data = {
              target_session_id: sessionId || null,
              subject_id: values.subject?.id.toString() ?? '',
              unit_id: values.unit?.id.toString() ?? '',  
              number: values.number?.id ?? '1',
              learning_objectives: values.learning_objectives,
              title: values.title,
              summary: values.summary,
              grade_level: values.grade_level?.id ?? '0',
              content_standard: values.content_standard,
              understandings: values.understandings,
              prior_knowledge: values.prior_knowledge,
              essential_questions: values.essential_questions,
              key_knowledge: values.key_knowledge,
              key_skills: values.key_skills,
              assessment_type: values.assessment_type?.id ?? '',
              assessment_evidence: values.assessment_evidence,
              other_evidence: values.other_evidence,
              diagnostic: values.diagnostic,
              specific_plan: values.specific_plan,
              established_goals_ids: values.topics.map(goal => goal.id),
              topics_ids: values.topics.map(topic => topic.id),
              options_ids: values.options.map(option => option.id),
              goalbank_selection: goalbankSelection,
              curriculum_selection: curriculumSelection
            }
            handleSubmit(action, id, data);
          }}
        >
          {({ values, setFieldValue, errors }) => {
            return (
              <Form style={{ marginTop: '1rem' }}>


                {entity && entity.id && preview ? <div>


                  <div id="lesson-plan">
                      <LessonPlanTemplate entity={entity} />
                  </div>

                  <Box sx={{ marginTop: '20px', display: 'flex' }} className="transparent-background">

                    {!sessionId &&
                      <Button
                          variant='outlined'
                          color='black'
                          onClick={() => navigate(backToSlug)}
                          sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                        >
                        <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.back')}`}
                      </Button> }


                      <Button
                        variant='outlined'
                        color='black'
                        onClick={ () => { printDiv('lesson-plan') } }
                          sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                        >
                        <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.print')}`}
                      </Button>


                      {!['student','parent'].includes(user?.role) &&
                      <Button
                        variant='outlined'
                        color='black'
                        onClick={ () => { setPreview(false) } }
                          sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                        >
                        <FaPencilAlt style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.edit')}`}
                      </Button>
                       }

                      {action === 'delete' && (
                        <SubmitButton
                          color={crudColorMap[action]}
                          text={`${t('form-actions.' + action)}`}
                          icon={crudIconMap[action]}
                          sx={{ width: '10rem' }}
                          isLoading={isLoading}
                        />
                      )}


                    </Box>
                  </div>

                  :

                  <>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6} lg={2}>
                        <SelectField
                            fieldProps={{ ...grade_level }}
                            apiCall={gradeSelectData}
                          />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <SelectField
                          fieldProps={{
                            ...subject,
                          }}
                          apiCall={useGET_subjectsSelectQuery}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={5}>
                        <SelectField
                          fieldProps={{
                            ...unit,
                          }}
                          apiCall={useGET_unitsSelectQuery}
                          filters={{
                            course__grade_level: values.grade_level?.id ?? '',
                            course__subject: values.subject?.id ?? '',
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={2}>
                        <SelectField
                            fieldProps={{ ...number }}
                            apiCall={lessonNumberSelectData}
                          />
                      </Grid>

                      <Grid item xs={12} md={8} lg={8}>
                        <InputField fieldProps={{ ...title }} />
                      </Grid>

                      <Grid item xs={12} md={4} lg={4}>
                          <Button
                              onClick={() => setGoalbankOpen(true)}
                              aria-label={`${t('goalbank.use-curriculum-library')}`}
                              size='small'
                              color='success'
                              variant='contained'
                              sx={{ boxShadow: 'none',  minHeight: '47px' }}
                              >
                                <FlagCircle style={{ fontSize: '1.5rem', marginRight: '10px' }} /> {t('goalbank.use-curriculum-library')}
                          </Button>
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <InputField fieldProps={{ ...summary }} multiline={true}  />
                      </Grid>

                      <Grid item xs={12}>
                        <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection}/>
                      </Grid>

                      <Grid item xs={12}>
                        <SectionTitle text={`${t('lessons.prior-knowledge')}`} 
                          icon={<InfoTooltip hint={t('lessons.prior-knowledge-info')}></InfoTooltip>}/>
                      </Grid>     
                      <Grid item xs={12} lg={12} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...prior_knowledge }} />
                      </Grid>

                      <Grid item xs={12}>
                        <SectionTitle text={`${t('lessons.meaning')}`} 
                          icon={<InfoTooltip hint={t('lessons.meaning-info')}></InfoTooltip>}/>
                      </Grid>     
                      <Grid item xs={12} lg={6} sx={{ paddingTop: '20px !important' }}>
                          <InputField multiline={true}  fieldProps={{ ...understandings }} 
                            hint={t('lessons.understandings-info')} />
                      </Grid>
                      <Grid item xs={12} lg={6} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...essential_questions }} 
                          hint={t('lessons.essential-questions-info')} />
                      </Grid>

                      {/*
                      <Grid item xs={12}>
                        <SectionTitle text={`${t('lessons.acquisition')}`} />
                      </Grid>     
                      <Grid item xs={12} lg={6} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...key_knowledge }} disabled={viewOnly} 
                          hint={t('lessons.key-knowledge')}/>
                      </Grid>
                      <Grid item xs={12} lg={6} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...key_skills }} disabled={viewOnly} 
                          hint={t('lessons.key-skills')}/>
                      </Grid>
                    */}


                      <Grid item xs={12}>
                        <SectionTitle text={`${t('lessons.evidence-and-assessment')}`} 
                          icon={<InfoTooltip hint={t('lessons.evidence-and-assessment-info')}></InfoTooltip> }/>
                      </Grid>     
                      <Grid item xs={12} lg={4} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                          fieldProps={{
                            ...assessment_type,
                          }}
                          apiCall={assessmentTypeSelectData}
                          hint={t('lessons.assessment-type-info')}
                        />
                      </Grid>
                      <Grid item xs={12} lg={4} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...assessment_evidence }} 
                          hint={t('lessons.assessment-evidence-info')}
                          />
                      </Grid>
                      <Grid item xs={12} lg={4} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...other_evidence }}
                          hint={t('lessons.other-evidence-info')}/>
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <SectionTitle text={`${t('lessons.learning-plan')}`} 
                          icon={<InfoTooltip hint={t('lessons.learning-plan-info')}></InfoTooltip> }/>
                      </Grid>     
                      <Grid item xs={12} lg={4} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...diagnostic }} 
                          hint={t('lessons.diagnostic-info')}/>
                      </Grid>
                      <Grid item xs={12} lg={4} sx={{ paddingTop: '20px !important' }}>
                        <SelectField
                          fieldProps={{
                            ...options,
                          }}
                          multiple={true}
                          apiCall={useGET_lessonPlanOptionsQuery}
                          hint={t('lessons.options-info')}
                        />
                      </Grid>
                      <Grid item xs={12} lg={4} sx={{ paddingTop: '20px !important' }}>
                        <InputField multiline={true}  fieldProps={{ ...specific_plan }}
                        hint={t('lessons.specific-plan-info')} />
                      </Grid>
                    </Grid>

                    {/* ACTIONS */}
                    <Box sx={{ marginTop: '20px', display: 'flex' }} className="transparent-background">
                      {!sessionId &&
                      <Button
                          variant='outlined'
                          color='black'
                          onClick={() => navigate(backToSlug)}
                          sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                        >
                        <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.back')}`}
                      </Button> }


                      {entity && entity.id && 
                        <Button
                          variant='outlined'
                          color='black'
                          onClick={() => setPreview(true)}
                          sx={{ minHeight: '2.5rem', width: '10rem' }}
                        >
                          <Visibility style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                          {`${t('common.preview')}`}
                        </Button>
                      }

                      {action !== 'view' && (
                        <SubmitButton
                          color={crudColorMap[action]}
                          text={`${t('form-actions.' + action)}`}
                          icon={crudIconMap[action]}
                          sx={{ width: '10rem' }}
                          isLoading={isLoading}
                        />
                      )}

                    </Box>
              
              

                    <DialogPopup
                      title={t('common.goalbank')}
                      open={goalbankOpen}
                      onClose={() => setGoalbankOpen(false)}
                      popupWidth={'1400px'}
                    >
                      <GoalbankPage hasSelection={true} filters={{
                            grade_level: values.grade_level?.id,
                            subject: values.subject?.id,
                      }}/>
                    </DialogPopup>
                    </>
                  }    
              </Form>
            )
          }}
        </Formik>
    </>
  )
}
export default LessonPlanForm
